import { motion, useScroll, useTransform } from "framer-motion";
import { BookOpen, Users, ScrollText, CalendarDays, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useCandle } from "@/lib/candle";
import CandleVisual from "@/components/CandleVisual";

const PORTRAIT = "/images/portrait.jpg";
const BANNER = "/images/croix.jpg";

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Banigb%C3%A9-Gare%2C+Ifangni%2C+B%C3%A9nin";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.35 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { scrollY } = useScroll();
  const { flame, count, light } = useCandle();
  const y = useTransform(scrollY, [0, 800], [0, 150]);

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-4 pb-20 pt-16 text-center"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={BANNER}
          alt="Croix dorée rayonnant dans les nuées — il repose en paix"
          className="h-[115%] w-full object-cover brightness-[0.8]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/65 via-[#1C1917]/40 to-[#1C1917]/85" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(28,25,23,0.62) 0%, rgba(28,25,23,0.25) 55%, rgba(28,25,23,0.45) 100%)",
          }}
        />
      </motion.div>

      <motion.img
        data-testid="hero-watermark"
        src={PORTRAIT}
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 0.18, scale: 1 }}
        transition={{ delay: 0.6, duration: 2 }}
        className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[80vh] w-[80vh] -translate-x-1/2 -translate-y-1/2 rounded-full object-cover object-top grayscale"
      />

      <div className="relative z-10 flex w-full flex-col items-center">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.8 }}
        className="mb-8 text-[11px] font-medium uppercase tracking-[0.35em] text-[#C5A059]"
      >
        Mémorium — Hommage éternel
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="relative mb-8"
      >
        <div className="absolute -inset-7 rounded-full bg-[#C5A059]/15 blur-xl animate-glow-pulse" />
        <div className="absolute -inset-4 animate-[spin_16s_linear_infinite] rounded-full border-2 border-dashed border-[#C5A059]/50" />
        <div className="absolute -inset-3 rounded-full border border-[#C5A059]/30" />
        <div className="absolute -inset-1.5 rounded-full border border-[#C5A059]/80" />
        <img
          data-testid="hero-portrait"
          src={PORTRAIT}
          alt="Portrait de François Dossa TANDJIEKPON"
          className="relative h-36 w-36 rounded-full object-cover object-top ring-1 ring-[#FAF8F5]/25 sm:h-44 sm:w-44"
        />
      </motion.div>

      <h1 data-testid="hero-name" className="leading-[1.05]">
        <span className="block overflow-hidden">
          <motion.span
            custom={0}
            variants={lineReveal}
            initial="hidden"
            animate="show"
            className="block font-display text-4xl font-medium text-[#FAF8F5] sm:text-5xl lg:text-6xl"
          >
            François Dossa
          </motion.span>
        </span>
        <span className="block overflow-hidden">
          <motion.span
            custom={1}
            variants={lineReveal}
            initial="hidden"
            animate="show"
            className="block font-display text-4xl font-semibold tracking-wide text-[#C5A059] sm:text-5xl lg:text-6xl"
          >
            TANDJIEKPON
          </motion.span>
        </span>
        <span className="mt-3 block overflow-hidden">
          <motion.span
            custom={2}
            variants={lineReveal}
            initial="hidden"
            animate="show"
            className="block font-display text-xl tracking-[0.3em] text-[#E8DCC8] sm:text-2xl"
          >
            LANWASSONOU
          </motion.span>
        </span>
      </h1>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.9 }}
        className="mt-6 flex items-center gap-4 text-sm tracking-[0.25em] text-[#E8DCC8]"
      >
        <span className="h-px w-10 bg-[#C5A059]/60" />
        <span data-testid="hero-dates" className="font-display text-base sm:text-lg">
          1938 — 18 Août 2026
        </span>
        <span className="h-px w-10 bg-[#C5A059]/60" />
      </motion.div>

      <motion.p
        data-testid="hero-role"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.25, duration: 0.9 }}
        className="mt-4 max-w-xl text-sm leading-relaxed text-stone-200 sm:text-base"
      >
        Patriarche, Opérateur Économique et Entrepreneur Agricole à la retraite
        — Banigbé-Gare, Ifangni, Bénin
      </motion.p>

      <motion.p
        data-testid="hero-repose"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.35, duration: 0.9 }}
        className="mt-4 font-display text-lg italic tracking-wide text-[#D4AF37]"
      >
        Il repose en paix — caveau familial de Banigbé-Gare
      </motion.p>

      <motion.blockquote
        data-testid="hero-quote"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.45, duration: 1 }}
        className="mt-8 max-w-lg font-display text-lg italic leading-relaxed text-[#E8DCC8]/90 sm:text-xl"
      >
        « Un grand arbre est tombé, mais ses racines continuent de nourrir nos
        mœurs. »
      </motion.blockquote>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.65, duration: 0.8 }}
        className="mt-10 flex flex-col items-center gap-6"
      >
        <div className="flex flex-col items-center">
          <CandleVisual
            lit={flame}
            size="md"
            big={flame}
            testId="hero-candle-flame"
            onClick={light}
            buttonTestId="hero-candle-button"
          />
          <p
            data-testid="hero-candle-hint"
            className="mt-3 max-w-[240px] text-center text-[11px] uppercase leading-relaxed tracking-[0.18em] text-[#E8DCC8]/70"
          >
            {flame
              ? "Sa flamme veille grâce à vous"
              : "Touchez la bougie pour l'allumer en sa mémoire"}
          </p>
          {count !== null && (
            <p
              data-testid="hero-candle-count"
              className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]/90"
            >
              {count} bougie{count > 1 ? "s" : ""} allumée{count > 1 ? "s" : ""} en sa mémoire
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="hero-condolences-btn"
            onClick={() => window.dispatchEvent(new Event("open-tribute-modal"))}
            className="flex items-center gap-2 rounded-full border border-[#FAF8F5]/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:border-[#FAF8F5] hover:bg-white/5"
          >
            <BookOpen className="h-4 w-4" /> Déposer un hommage
          </button>
          <button
            data-testid="hero-tree-btn"
            onClick={() => scrollToId("famille")}
            className="flex items-center gap-2 rounded-full border border-[#FAF8F5]/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:border-[#FAF8F5] hover:bg-white/5"
          >
            <Users className="h-4 w-4" /> Arbre généalogique
          </button>
          <button
            data-testid="hero-story-btn"
            onClick={() => scrollToId("histoire")}
            className="flex items-center gap-2 rounded-full border border-[#FAF8F5]/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:border-[#FAF8F5] hover:bg-white/5"
          >
            <ScrollText className="h-4 w-4" /> Histoire du Patriarche
          </button>
          <button
            data-testid="hero-program-btn"
            onClick={() => scrollToId("programme")}
            className="flex items-center gap-2 rounded-full border border-[#FAF8F5]/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:border-[#FAF8F5] hover:bg-white/5"
          >
            <CalendarDays className="h-4 w-4" /> Programme des obsèques
          </button>
          <a
            data-testid="hero-route-btn"
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-[#C5A059]/70 px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#1C1917]"
          >
            <MapPin className="h-4 w-4" /> Itinéraire vers Banigbé-Gare
          </a>
        </div>
      </motion.div>
      </div>
    </section>
  );
}
